import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Cancel as CancelIcon,
  Edit as EditIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { FlagDefinition } from '@/API.ts';
import log from 'loglevel';

interface Props {
  flagDefinition: FlagDefinition;
  onUpdate: (updated: FlagDefinition) => Promise<void>;
  isEditing: boolean;
  onEditToggle: (editing: boolean) => void;
}

const FlagDefinitionCard: React.FC<Props> = ({
  flagDefinition,
  onUpdate,
  isEditing,
  onEditToggle,
}) => {
  const [editedDef, setEditedDef] =
    useState<Partial<FlagDefinition>>(flagDefinition);
  const [isSaving, setIsSaving] = useState(false);

  // Sync local state when flagDefinition prop changes
  useEffect(() => {
    setEditedDef(flagDefinition);
  }, [flagDefinition]);

  const handleSave = async () => {
    if (!editedDef.friendlyName?.trim()) {
      alert('Friendly name is required');
      return;
    }

    setIsSaving(true);
    try {
      await onUpdate({
        ...flagDefinition,
        ...editedDef,
        friendlyName: editedDef.friendlyName.trim(),
      });
      onEditToggle(false);
    } catch (error) {
      log.error('Failed to update flag definition:', error);
      alert('Failed to update flag definition');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditedDef(flagDefinition);
    onEditToggle(false);
  };

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Box flex={1}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Flag Value: {flagDefinition.flagValue}
            </Typography>

            {isEditing ? (
              <Box sx={{ mt: 2 }}>
                <TextField
                  fullWidth
                  label="Friendly Name"
                  value={editedDef.friendlyName || ''}
                  onChange={e =>
                    setEditedDef({ ...editedDef, friendlyName: e.target.value })
                  }
                  margin="normal"
                  required
                />
                <TextField
                  fullWidth
                  label="Description"
                  value={editedDef.description || ''}
                  onChange={e =>
                    setEditedDef({ ...editedDef, description: e.target.value })
                  }
                  margin="normal"
                  multiline
                  rows={2}
                />
              </Box>
            ) : (
              <Box sx={{ mt: 1 }}>
                <Typography variant="h6">
                  {flagDefinition.friendlyName}
                </Typography>
                {flagDefinition.description && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    {flagDefinition.description}
                  </Typography>
                )}
              </Box>
            )}
          </Box>

          <Box ml={2}>
            {isEditing ? (
              <Box>
                <Tooltip title="Save">
                  <IconButton
                    onClick={handleSave}
                    disabled={isSaving}
                    color="primary"
                  >
                    <SaveIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Cancel">
                  <IconButton onClick={handleCancel} disabled={isSaving}>
                    <CancelIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            ) : (
              <Tooltip title="Edit">
                <IconButton onClick={() => onEditToggle(true)} color="primary">
                  <EditIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default FlagDefinitionCard;
